import { Component, inject, OnDestroy, OnInit, Type } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { map, Observable, Subject, Subscription, shareReplay, tap } from 'rxjs';
import { FASDynamicTableColumnConfig } from 'fas-services';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { IMyOskStudentDetailsVm } from '../../../../api/my-osk-student-details-vm.model';
import { MyOskStudentDetailsService } from '../../../../api/api-services/my-osk-student-details.service';
import { eModalRoles } from '../../../shared/models/modals';
import { MessageService } from 'primeng/api';
import { IPaymentVm } from '../../../../api/payment-vm.model';
import { IWordExamVm } from '../../../../api/word-exam-vm.model';
import { IStudentCategoryProgressVm } from '../../../../api/student-category-progress-vm.model';
import { IStudentLecturesProgressVm } from '../../../../api/student-lectures-progress-vm.model';
import { ExamDateComponent } from '../exam-date/exam-date.component';
import { centerDynamicModalConfig } from '../../../shared/helpers/modal.config';
import { UserWordExamService } from '../../../../api/api-services/user-word-exam.service';
import { ExamViewComponent } from '../exam-view/exam-view.component';
import { IMyOskStudentVm } from '../../../../api/my-osk-student-vm.model';
import { StudentsAssignSubscriptionComponent } from '../students-assign-subscription/students-assign-subscription.component';
import { IMyOskStudentDetailsUm } from '../../../../api/my-osk-student-details-um.model';
import { OskStudentWordExamService } from '../../../../api/api-services/osk-student-word-exam.service';
import { InternalExamResult, InternalExamSource } from '../../../../api/internal-exam-classification.model';
import { StudentDocumentsPdfService } from '../../../shared/services/student-documents-pdf.service';
import { AvailableExamsService } from '../../../../api/api-services/available-exams.service';
import { MyOskEmployeesService } from '../../../../api/api-services/my-osk-employees.service';
import { MyOskEmployeeStudentService } from '../../../../api/api-services/my-osk-employee-student.service';
import { IMyOskStudentEmployeeVm } from '../../../../api/my-osk-employee-student.model';
import { isTeachingRole, OSK_EMPLOYEE_ROLE_LABELS, OskEmployeeRole } from '../../../../api/osk-employee-role.model';
import { canAdministerStudents, canUseDrivingCalendar, isOskBoss } from '../../../shared/helpers/osk-staff-role';

@Component({
  selector: 'app-students-details',
  templateUrl: './students-details.component.html',
  styleUrl: './students-details.component.scss',
})
export class StudentsDetailsComponent implements OnInit, OnDestroy {
  private _mainDialogRef = inject(DynamicDialogRef);
  private _confifg = inject(DynamicDialogConfig);
  private _myOskStudentDetailsService = inject(MyOskStudentDetailsService);
  private _messageService = inject(MessageService);
  private _primengDialogService = inject(DialogService);
  private _oskStudentWordExamService = inject(OskStudentWordExamService);
  private _fb = inject(FormBuilder);
  private _studentDocumentsPdfService = inject(StudentDocumentsPdfService);
  private _availableExamsService = inject(AvailableExamsService);
  private _employeesService = inject(MyOskEmployeesService);
  private _employeeStudentService = inject(MyOskEmployeeStudentService);
  private additionalDialogRef: DynamicDialogRef | undefined;

  /** Student data, documents and access - the boss and the office; instructors and lecturers only read. */
  public readonly canAdministerStudents = canAdministerStudents();
  /** Internal exams (viewing, results, dates) stay with the boss. */
  public readonly isBoss = isOskBoss();
  /** "Jazdy" tab (index 3): the boss, the office and the instructors. */
  public readonly canUseDrivingCalendar = canUseDrivingCalendar();
  /** The "Jazdy" tab component, imported when the tab is first opened (a chunk of its own). */
  public drivingLessonsTab: Type<unknown> | null = null;
  /** Instructors and lecturers of the OSK, for assigning the student. */
  public teacherOptions: { label: string; value: string }[] = [];
  public selectedTeacherGuid: string | null = null;
  public assigningTeacher = false;

  public data: {
    currentStudentIndex: number;
    studentListPerPage: IMyOskStudentVm[];
  } = this._confifg.data;

  public hasStudentExamsData: boolean = true;
  public refreshTable = new Subject<void>();
  public oskStudentDetails$!: Observable<IMyOskStudentDetailsVm>;
  public selectedTabIndex: number = 0;
  public studentDetailsForm!: FormGroup;
  public editStudentDataMode: boolean = false;
  public loading: boolean = false;
  public documentLoading: 'agreement' | 'rodo' | null = null;
  public cardsAddedForm!: FormGroup;
  public currentStudentIndex: number = this.data.currentStudentIndex;
  public currentStudentDetails: IMyOskStudentDetailsVm | null = null;
  /** Driving licence categories for the editable "Kategoria" field. */
  public examCategoryOptions: { label: string; value: string }[] = [];
  public formFields: {
    name: keyof IMyOskStudentDetailsVm;
    label: string;
    readonly: boolean;
  }[] = [
    { name: 'displayName', label: 'Nazwa użytkownika', readonly: true },
    { name: 'created', label: 'Data utworzenia', readonly: true },
    { name: 'firstName', label: 'Imię', readonly: true },
    { name: 'lastName', label: 'Nazwisko', readonly: true },
    { name: 'pkk', label: 'PKK', readonly: true },
    { name: 'pesel', label: 'Pesel', readonly: true },
    { name: 'phone', label: 'Numer telefonu', readonly: true },
    { name: 'examCategoryGuid', label: 'Kategoria', readonly: true },
    // Registered address, filled into the agreements generated for the student.
    { name: 'street', label: 'Ulica i numer', readonly: true },
    { name: 'postalCode', label: 'Kod pocztowy', readonly: true },
    { name: 'city', label: 'Miasto', readonly: true },
    { name: 'email', label: 'Adres e-mail', readonly: true },
  ];

  public studentPaymentsColumns: FASDynamicTableColumnConfig<IPaymentVm>[] = [
    {
      field: 'finishedDate',
      columnHeader: 'Data płatności',
      dateField: true,
    },
    {
      field: 'value',
      columnHeader: 'Kwota',
      valueFunction: (data: IPaymentVm) => data.value + ' zł',
    },
    {
      field: 'success',
      columnHeader: 'Status transakcji',
      columnTemplateKey: 'paymentStatusTemp',
    },
    {
      field: 'paymentOperator',
      columnHeader: 'Operator',
    },
  ];

  public studentExamColumns: FASDynamicTableColumnConfig<IWordExamVm>[] = [
    {
      field: 'end',
      columnHeader: 'Data wykonania egzaminu',
      columnTemplateKey: 'examDateTemp',
    },
    {
      field: 'earnedPoints',
      columnHeader: 'Punkty',
      valueFunction: (data: IWordExamVm) =>
        `${data.earnedPoints ?? 0}/${data.availablePoints ?? 0}`,
    },
    {
      columnHeader: 'Akcje',
      columnTemplateKey: 'examActionsTemp',
    },
  ];

  public studentCategoryProgressColumns: FASDynamicTableColumnConfig<IStudentCategoryProgressVm>[] =
    [
      {
        field: 'categoryName',
        columnHeader: 'Dział',
      },
      {
        field: 'questionsLearned',
        columnHeader: 'Slajd',
        valueFunction: (data: IStudentCategoryProgressVm) =>
          `${data.questionsLearned ?? 0}/${data.questionsInCategory ?? 0}`,
      },
    ];

  public studentLecturesProgressColumns: FASDynamicTableColumnConfig<IStudentLecturesProgressVm>[] =
    [
      {
        field: 'lectureName',
        columnHeader: 'Dział',
      },
      {
        field: 'lecturesLearned',
        columnHeader: 'Slajd',
        valueFunction: (data: IStudentLecturesProgressVm) =>
          `${data.lecturesLearned ?? 0}/${data.lecturesInCategory ?? 0}`,
      },
      {
        field: 'overallPercentageProgress',
        columnHeader: 'Postęp',
        valueFunction: (data: IStudentLecturesProgressVm) =>
          `${data.overallPercentageProgress ?? 0}%`,
      },
    ];

  private currentStudent: IMyOskStudentDetailsVm | null = null;
  private studentDetailsSubscription?: Subscription;

  ngOnInit(): void {
    // The API sends no PESEL and no address to instructors and lecturers - the rows would only stay empty.
    if (!this.canAdministerStudents) {
      const hidden: Array<keyof IMyOskStudentDetailsVm> = ['pesel', 'street', 'postalCode', 'city'];
      this.formFields = this.formFields.filter((field) => !hidden.includes(field.name));
    }
    this.buildForm();
    this.getCurrentStudentDetails();
    this.loadExamCategories();
    if (this.canAdministerStudents) {
      this.loadTeachers();
    }
  }

  ngOnDestroy(): void {
    this.studentDetailsSubscription?.unsubscribe();
    if (this.additionalDialogRef) {
      this.additionalDialogRef.close(); // Closes the dialog if it's still open
      this.additionalDialogRef = undefined;
    }
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    if (index === 3 && this.canUseDrivingCalendar && !this.drivingLessonsTab) {
      import('../student-driving-lessons/student-driving-lessons.component')
        .then((m) => (this.drivingLessonsTab = m.StudentDrivingLessonsComponent))
        .catch(() => this._messageService.add({ severity: 'error', detail: 'Nie udało się wczytać zakładki Jazdy.' }));
    }
  }

  closeDialog(status: eModalRoles = eModalRoles.CLOSE): void {
    this._mainDialogRef.close(status);
  }

  editStudentData(): void {
    if (!this.currentStudent) return;

    this.editStudentDataMode = true;
    this.patchDataToForm(this.currentStudent);
    this.updateFormFields(true);
    this.updateFormControlsStatus();
  }

  cancelStudentDataChanges(): void {
    this.editStudentDataMode = false;
    this.updateFormFields(false);
    if (this.currentStudent) {
      this.patchDataToForm(this.currentStudent);
    }
    this.updateFormControlsStatus();
  }

  updateStudentData(): void {
    if (!this.currentStudent || this.studentDetailsForm.invalid) return;

    this.loading = true;
    const studentGuid =
      this.data.studentListPerPage[this.currentStudentIndex].guid;
    const { pkk, firstName, lastName, pesel, phone, examCategoryGuid, street, postalCode, city } =
      this.studentDetailsForm.getRawValue();

    const payload: IMyOskStudentDetailsUm = {
      guid: studentGuid,
      firstName: (firstName ?? '').trim(),
      lastName: (lastName ?? '').trim(),
      pkk: (pkk ?? '').trim(),
      pesel: (pesel ?? '').trim(),
      phone: (phone ?? '').trim(),
      examCategoryGuid: examCategoryGuid || undefined,
      // An empty string clears the stored address line.
      street: (street ?? '').trim(),
      postalCode: (postalCode ?? '').trim(),
      city: (city ?? '').trim(),
    }
    this._myOskStudentDetailsService.put(payload).subscribe({
      next: (updatedStudent) => {
        this.loading = false
        this.editStudentDataMode = false;
        this.currentStudent = updatedStudent;
        this.currentStudentDetails = updatedStudent;
        this.updateFormFields(false);
        this.patchDataToForm(updatedStudent);
        this.updateFormControlsStatus();
        this._messageService.add({
          severity: 'success',
          detail: 'Dane użytkownika zostały zaktualizowane',
        })
        this.getCurrentStudentDetails();
      },
      error: (err) => {
        this.loading = false;
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: err?.error?.message ?? 'Nie udało się zaktualizować danych kursanta.',
        })
      }
    })
  }

  moveUp(): void {
    this.currentStudentIndex =
      (this.currentStudentIndex + 1) % this.data.studentListPerPage.length;
    this.getCurrentStudentDetails();
  }

  moveDown(): void {
    this.currentStudentIndex =
      (this.currentStudentIndex - 1 + this.data.studentListPerPage.length) %
      this.data.studentListPerPage.length;
    this.getCurrentStudentDetails();
  }

  getCurrentStudentDetails(): void {
    this.studentDetailsSubscription?.unsubscribe();
    this.currentStudentDetails = null;
    this.currentStudent = null;
    this.editStudentDataMode = false;
    this.updateFormFields(false);
    this.updateFormControlsStatus();
    this.oskStudentDetails$ = this.getStudentDetails$().pipe(
      tap((res: IMyOskStudentDetailsVm) => {
        this.currentStudent = res;
        this.patchDataToForm(res);
        this.updateFormControlsStatus();
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.studentDetailsSubscription = this.oskStudentDetails$.subscribe({
      next: () => this.refreshTable.next(),
      error: () => this._messageService.add({ severity: 'error', detail: 'Nie udało się pobrać danych kursanta.' }),
    });
  }

  getStudentDetails$(): Observable<IMyOskStudentDetailsVm> {
    const studentGuid =
      this.data.studentListPerPage[this.currentStudentIndex].guid;
    return this._myOskStudentDetailsService.get(studentGuid).pipe(
      tap((res: IMyOskStudentDetailsVm) => {
        this.currentStudentDetails = res;
      })
    );
  }

  fetchStudentPayments = (): Observable<IPaymentVm[]> => {
    return this.oskStudentDetails$.pipe(
      map((res: IMyOskStudentDetailsVm) => res.studentPayments ?? [])
    );
  };

  fetchStudentExams = (): Observable<IWordExamVm[]> => {
    return this.oskStudentDetails$.pipe(
      map((res: IMyOskStudentDetailsVm) => {
        const exams = res.userWordExams ?? [];
        this.hasStudentExamsData = exams.length > 0;
        return exams;
      })
    );
  };

  fetchStudentCategoryProgress = (): Observable<
    IStudentCategoryProgressVm[]
  > => {
    return this.oskStudentDetails$.pipe(
      map((res: IMyOskStudentDetailsVm) => res.studentCategoryProgress ?? [])
    );
  };

  fetchStudentLecturesProgress = (): Observable<
    IStudentLecturesProgressVm[]
  > => {
    return this.oskStudentDetails$.pipe(
      map((res: IMyOskStudentDetailsVm) => res.studentLecturesProgress ?? [])
    );
  };

  roleLabel(role: OskEmployeeRole): string {
    return OSK_EMPLOYEE_ROLE_LABELS[role] ?? '';
  }

  /** Teachers the student is not assigned to yet. */
  availableTeacherOptions(): { label: string; value: string }[] {
    const assigned = new Set((this.currentStudentDetails?.assignedEmployees ?? []).map((x) => x.employeeGuid));
    return this.teacherOptions.filter((option) => !assigned.has(option.value));
  }

  assignTeacher(): void {
    if (!this.currentStudentDetails || !this.selectedTeacherGuid) return;
    this.assigningTeacher = true;
    this._employeeStudentService
      .post({ employeeGuid: this.selectedTeacherGuid, studentGuid: this.currentStudentDetails.guid })
      .subscribe({
        next: (assigned) => this.onAssignmentsChanged(assigned, 'Kursant został przypisany.'),
        error: (err) => this.onAssignmentError(err),
      });
  }

  unassignTeacher(employee: IMyOskStudentEmployeeVm): void {
    if (!this.currentStudentDetails) return;
    this.assigningTeacher = true;
    this._employeeStudentService.delete(employee.employeeGuid, this.currentStudentDetails.guid).subscribe({
      next: (assigned) => this.onAssignmentsChanged(assigned, 'Kursant został odpięty.'),
      error: (err) => this.onAssignmentError(err),
    });
  }

  assignSubscription(): void {
    if (this.currentStudentDetails) {
      this.openAssignSubscriptionModal(this.currentStudentDetails);
    }
  }

  async downloadAgreement(): Promise<void> {
    if (!this.currentStudentDetails) return;
    this.documentLoading = 'agreement';
    try {
      await this._studentDocumentsPdfService.downloadAgreement(this.currentStudentDetails);
    } catch {
      this.handleDocumentError();
    } finally {
      this.documentLoading = null;
    }
  }

  async downloadRodo(): Promise<void> {
    if (!this.currentStudentDetails) return;
    this.documentLoading = 'rodo';
    try {
      await this._studentDocumentsPdfService.downloadRodo(this.currentStudentDetails);
    } catch {
      this.handleDocumentError();
    } finally {
      this.documentLoading = null;
    }
  }

  sendActivationEmail(): void {
    this._messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: 'Email activation to be implemented',
    });
  }

  passwordReset(): void {
    this._messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: 'Password reset to be implemented',
    });
  }

  getStudentPaymentStatus(rowData: IPaymentVm): {
    label: string;
    color: string;
  } {
    if (rowData.finished && rowData.success) {
      return {
        label: 'Zakończona pomyślnie',
        color: 'text-green-500',
      };
    } else if (rowData.finished && !rowData.success) {
      return {
        label: 'Nieudana',
        color: 'text-red-500',
      };
    } else {
      return {
        label: 'W trakcie realizacji',
        color: 'text-gray-500',
      };
    }
  }

  isPassed(result?: InternalExamResult): boolean {
    return result === InternalExamResult.PassedByOsk ||
      result === InternalExamResult.PassedAutomatically;
  }

  isFailed(result?: InternalExamResult): boolean {
    return result === InternalExamResult.FailedByOsk ||
      result === InternalExamResult.FailedAutomatically;
  }

  hasInternalExamResult(rowData: IWordExamVm): boolean {
    return rowData.internalExamResult !== undefined &&
      rowData.internalExamResult !== null &&
      rowData.internalExamResult !== InternalExamResult.Undefined;
  }

  getInternalExamSourceIcon(source?: InternalExamSource): string {
    return source === InternalExamSource.Osk ? 'pi-car' : 'pi-home';
  }

  getInternalExamStatusLabel(rowData: IWordExamVm): string {
    const resultLabels: Record<InternalExamResult, string> = {
      [InternalExamResult.Undefined]: 'Nieokreślony',
      [InternalExamResult.PassedByOsk]: 'Zaliczony',
      [InternalExamResult.PassedAutomatically]: 'Zaliczony',
      [InternalExamResult.FailedByOsk]: 'Niezaliczony',
      [InternalExamResult.FailedAutomatically]: 'Niezaliczony',
    };
    const source = rowData.internalExamSource === InternalExamSource.Osk
      ? 'źródło: OSK'
      : 'źródło: egzamin własny';
    return `${resultLabels[rowData.internalExamResult]} — ${source}`;
  }

  canMarkInternalExam(rowData: IWordExamVm): boolean {
    const endTime = rowData.end ? new Date(rowData.end).getTime() : Number.NaN;
    return rowData.internalExamSource === InternalExamSource.Independent &&
      rowData.internalExamResult === InternalExamResult.Undefined &&
      Number.isFinite(endTime) &&
      endTime <= Date.now();
  }

  getManualResult(rowData: IWordExamVm): InternalExamResult {
    return rowData.earnedPoints >= rowData.minimumPoints
      ? InternalExamResult.PassedByOsk
      : InternalExamResult.FailedByOsk;
  }

  getManualResultTooltip(rowData: IWordExamVm): string {
    return this.getManualResult(rowData) === InternalExamResult.PassedByOsk
      ? 'Oznacz jako zaliczony egzamin wewnętrzny'
      : 'Oznacz jako niezaliczony egzamin wewnętrzny';
  }

  markInternalExam(rowData: IWordExamVm): void {
    this._oskStudentWordExamService
      .setResult(rowData.guid, this.getManualResult(rowData))
      .subscribe({
        next: () => {
          this._messageService.add({
            severity: 'success',
            detail: 'Status egzaminu został zapisany.',
          });
          this.refreshTable.next();
        },
        error: (err) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: err?.error?.message ?? 'Nie udało się zmienić statusu egzaminu.',
          });
        },
      });
  }

  showExam(rowData: IWordExamVm, rowIndex: number): void {
    this.exammRowAction(rowData, rowIndex, 'show');
  }

  changeExamDate(rowData: IWordExamVm, rowIndex: number): void {
    this.exammRowAction(rowData, rowIndex, 'changeDate');
  }

  private exammRowAction(
    rowData: IWordExamVm,
    rowIndex: number,
    actionType: 'show' | 'changeDate' | 'print'
  ): void {
    if (actionType === 'changeDate') {
      this.openChangeExamDateModal(rowData.guid, rowData.end ?? new Date());
    } else {
      // rowData.guid = '08dd37ce-32d2-4b9b-8f3f-0387c5f00ad1';
      this._oskStudentWordExamService.get(rowData.guid, true).subscribe({
        next: (res: IWordExamVm) => {
          this.openShowExamModal(res);
        },
        error: () => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Wystąpił błąd. Spróbuj ponownie później.',
          });
        },
      });
    }
  }

  private openChangeExamDateModal(
    studentWordExamGuid: string,
    finishedDate: Date
  ): void {
    this.additionalDialogRef = this._primengDialogService.open(
      ExamDateComponent,
      {
        ...centerDynamicModalConfig,
        width: '579px',
        height: '695px',
        baseZIndex: 10001,
        data: {
          studentWordExamGuid,
          finishedDate,
        },
      }
    );

    this.additionalDialogRef.onClose.subscribe(
      (res: { status: eModalRoles; examEndDate: Date }) => {
        if (res.status === eModalRoles.REFRESH) {
          this.refreshTable.next();
        }
      }
    );
  }

  private openShowExamModal(data: IWordExamVm): void {
    this.additionalDialogRef = this._primengDialogService.open(
      ExamViewComponent,
      {
        ...centerDynamicModalConfig,
        data: {
          wordExamDetails: data,
        },
        width: '90vw',
        height: '90vh',
        baseZIndex: 10001,
      }
    );

    this.additionalDialogRef.onClose.subscribe((res: eModalRoles) => {
      if (res === eModalRoles.REFRESH) {
        this.refreshTable.next();
      }
    });
  }

  private openAssignSubscriptionModal(studentDetails: IMyOskStudentVm) {
    this.additionalDialogRef = this._primengDialogService.open(
      StudentsAssignSubscriptionComponent,
      {
        ...centerDynamicModalConfig,
        width: '966px',
        data: {
          studentDetails,
        },
      }
    );

    this.additionalDialogRef.onClose.subscribe({
      next: (res: eModalRoles | undefined) => {
        if (res === eModalRoles.SUCCESS) {
          this.getCurrentStudentDetails();
        }
      },
    });
  }

  private loadTeachers(): void {
    this._employeesService.get().subscribe({
      next: (employees) => {
        this.teacherOptions = employees
          .filter((employee) => !!employee.guid && isTeachingRole(employee.role))
          .map((employee) => ({
            label: `${employee.displayName} (${this.roleLabel(employee.role)})`,
            value: employee.guid!,
          }));
      },
      error: () => (this.teacherOptions = []),
    });
  }

  private onAssignmentsChanged(assigned: IMyOskStudentEmployeeVm[], message: string): void {
    this.assigningTeacher = false;
    this.selectedTeacherGuid = null;
    if (this.currentStudentDetails) {
      this.currentStudentDetails = { ...this.currentStudentDetails, assignedEmployees: assigned };
    }
    this._messageService.add({ severity: 'success', detail: message });
  }

  private onAssignmentError(err: any): void {
    this.assigningTeacher = false;
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: err?.error?.message ?? 'Nie udało się zmienić przypisania.',
    });
  }

  private buildForm(): void {
    const formGroup: any = {};
    this.formFields.forEach((field) => {
      formGroup[field.name] = [''];
    });
    this.studentDetailsForm = this._fb.group(formGroup);
  }

  private loadExamCategories(): void {
    this._availableExamsService.get().subscribe({
      next: (exams) => {
        this.examCategoryOptions = exams
          .filter((exam) => exam.active)
          .map((exam) => ({ label: exam.examName, value: exam.guid }));
      },
      error: () =>
        this._messageService.add({
          severity: 'warn',
          summary: 'Uwaga',
          detail: 'Nie udało się pobrać listy kategorii.',
        }),
    });
  }

  private patchDataToForm(student: IMyOskStudentDetailsVm): void {
    const { pkk, pesel, firstName, lastName, phone, examCategoryGuid, street, postalCode, city } = student;

    const data = {
      pkk,
      pesel,
      firstName,
      lastName,
      phone,
      examCategoryGuid: examCategoryGuid ?? null,
      street: street ?? '',
      postalCode: postalCode ?? '',
      city: city ?? '',
    };

    this.studentDetailsForm.patchValue(data);
  }

  private updateFormFields(editable: boolean): void {
    const editableFields: Array<keyof IMyOskStudentDetailsVm> = [
      'firstName',
      'lastName',
      'pkk',
      'pesel',
      'phone',
      'examCategoryGuid',
      'street',
      'postalCode',
      'city',
    ];

    this.formFields = this.formFields.map(field => {
      if (editableFields.includes(field.name)) {
        return { ...field, readonly: !editable };
      }
      return field;
    });
  }

  private updateFormControlsStatus(): void {
    const controlsToDisable = [
      'firstName',
      'lastName',
      'pkk',
      'pesel',
      'phone',
      'examCategoryGuid',
      'street',
      'postalCode',
      'city',
    ];

    controlsToDisable.forEach((controlName) => {
      const control = this.studentDetailsForm.get(controlName);
      if (control) {
        if (this.editStudentDataMode) {
          control.enable();
        } else {
          control.disable();
        }
      }
    });
  }

  private handleDocumentError(): void {
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się przygotować dokumentu PDF. Spróbuj ponownie.',
    });
  }
}
